import requestWrapper from './requestWrapper';
import { client, fileClient, noProxyClient } from './requesters';

import ErrorRMW from '../model/ErrorRMW';
import Constants from '../constants';
import SelectPropertyConstants from '../constants/SelectPropertyConstants';
import queryUtils from './queryUtils';
import { RENTAL_MANAGER_API_V1_PREFIX, RENTAL_MANAGER_API_V2_PREFIX } from '../constants/RouteConstants';

const { GET, POST } = Constants;

const api = {
  waitlist: {
    startWaitlist(listingAlias, customerId) {
      const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/waitlist/setWaitlist`;
      return requestWrapper(client, GET, endpoint, {
        params: {
          aliasEncoded: listingAlias,
          customerId,
        },
      });
    },
    endWaitlist(listingAlias, customerId) {
      const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/waitlist/removeWaitlist`;
      return requestWrapper(client, GET, endpoint, {
        params: {
          aliasEncoded: listingAlias,
          customerId,
        },
      });
    },
  },
  multibrandSupport: {
    appConfig(params) {
      const { appName = 'zrm_web' } = params;
      const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/multibrandSupport/appConfig`;
      return requestWrapper(client, GET, endpoint, { params: { appName } });
    },
  },
  properties: {
    address: {
      updateAddress(data) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/address`;

        return requestWrapper(client, GET, endpoint, {
          params: {
            propertyId: data.propertyId,
            fullAddress: data.fullAddress,
            unitNumber: data.unitNumber,
            propertyTypeCode: data.propertyTypeCode,
          },
        });
      },
    },

    locations: {
      getRestrictions(data) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/locations/restrictions`;

        return requestWrapper(client, GET, endpoint, {
          params: {
            fullAddress: data.address,
            unit: data.unit,
            listingTypeCode: data.listingTypeCode,
            propertyTypeCode: data.propertyTypeCode,
            listingAlias: data.listingAlias,
          },
        });
      },
    },
    rentGuarantee: {
      getDisclaimers(listingAlias, disclaimerTypes) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/getDisclaimers`;

        return requestWrapper(client, POST, endpoint, {
          data: { listingAlias, disclaimerTypes },
        });
      },
      storeListingPremiumCoverageSelection({ listingAlias, premiumCoverageId }) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/storeListingPremiumCoverageSelection`;

        return requestWrapper(client, POST, endpoint, {
          data: { listingAlias, premiumCoverageId },
        });
      },
      updatePremiumCoverageSelectionForPolicy({ listingAlias, policyId, premiumCoverageId }) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/updatePremiumCoverageSelectionForPolicy`;

        return requestWrapper(client, POST, endpoint, {
          data: { listingAlias, policyId, premiumCoverageId },
        });
      },
      dropRentalProtectionPolicy(dataToApi) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/dropRentalProtectionPolicy`;

        return requestWrapper(client, POST, endpoint, dataToApi);
      },
      reinstatePolicy(dataToApi) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/reinstatePolicy`;

        return requestWrapper(client, POST, endpoint, dataToApi);
      },
      storeEffectivePolicyScreenDisplayed(
        params = {
          listingAlias: '',
          userToken: '',
        },
      ) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/storeEffectivePolicyScreenDisplayed`;

        return requestWrapper(client, POST, endpoint, params);
      },
      getSecureClaimsUrlForPolicy(
        params = {
          policyId: '',
          listingAlias: '',
        },
      ) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/getSecureClaimsUrlForPolicy`;

        return requestWrapper(client, GET, endpoint, { params });
      },
      updateSecurityDepositForListing(propertyId, securityDeposit) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/securityDeposit/update`;
        const params = {
          propertyId,
          securityDeposit,
        };
        return requestWrapper(client, GET, endpoint, { params });
      },
      updateZrmToolsAndSecurityDepositReviewed(listingAlias) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/updateZrmToolsAndSecurityDepositReviewed`;
        const data = {
          listingAlias,
        };
        return requestWrapper(client, POST, endpoint, { data });
      },
      updateOrWaiveSecurityDepositForListing(
        listingAlias,
        securityDeposit,
        waiveSecurityDeposit,
        reviewedListingSecurityDepositForLightweight,
      ) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/rentGuarantee/securityDeposit/updateOrWaive`;
        const data = {
          listingAlias,
          securityDeposit,
          waiveSecurityDeposit,
          reviewedListingSecurityDepositForLightweight,
        };
        return requestWrapper(client, POST, endpoint, { data });
      },
    },
  },
  users: {
    profile: {
      setName(
        params = {
          firstName: '',
          lastName: '',
        },
      ) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/profile/setName`;

        return requestWrapper(client, GET, endpoint, { params });
      },
      saveUserInfo(
        params = {
          firstName: '',
          lastName: '',
          isLandlord: false,
          isPropertyManager: false,
          propertyCount: 0,
        },
      ) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/profile/saveUserInfo`;

        return requestWrapper(client, GET, endpoint, { params });
      },
    },
    signUrl(url, params = {}) {
      const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/multibrandSupport/googleMaps`;
      const { brandName } = params;
      return requestWrapper(client, POST, endpoint, {
        params: { brandName },
        data: { url },
      });
    },

    applications: {
      inviteToApply(data = {}) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/capps/handleApplicationInvitation`;

        return requestWrapper(client, POST, endpoint, { data });
      },
      copyApplicationLinkApi(data = {}) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/capps/handleApplicationInvitationLink`;

        return requestWrapper(client, POST, endpoint, { data });
      },
    },
    contacts: {
      verifier: {
        confirm(data) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/contacts/verifier/confirm`;

          return requestWrapper(client, GET, endpoint, {
            params: {
              contact: data.phone,
              contactType: 'phone',
              verificationCode: data.verificationCode,
              verificationSource: data.verificationSource,
            },
          });
        },
        send(data) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/contacts/verifier/send`;

          return requestWrapper(client, GET, endpoint, {
            params: {
              contact: data.phone,
              contactType: 'phone',
              verificationType: data.verificationType,
              verificationSource: data.verificationSource,
            },
          });
        },
      },
    },
    // Used for retargeting purposes
    trackEvent({ trackingEvent }) {
      const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/tracking`;

      return requestWrapper(client, GET, endpoint, { params: { trackingEvent } });
    },
    ip(skipClientProxy) {
      const endpoint = 'rental-manager-api/datarouter/ipDetection';

      const request = skipClientProxy ? noProxyClient : client;
      return requestWrapper(request, GET, endpoint);
    },
    intention: {
      insertAction(data) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/intention/insertAction`;

        return requestWrapper(client, POST, endpoint, { data });
      },
    },

    referrals: {
      add(params) {
        const { code, type = 'paidInclusionSales' } = params;
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/referrals/add`;
        let hasError = false;
        let message = null;

        // check for required params
        if (!code) {
          hasError = true;
          message = 'code is required';
        }

        // check if code is alphanumeric
        if (hasError === false && queryUtils.isAlphaNumeric(code) === false) {
          hasError = true;
          message = 'code must be alphanumeric';
        }

        if (hasError) {
          return Promise.reject(
            ErrorRMW({
              message,
              data: params,
            }),
          );
        }

        return requestWrapper(client, GET, endpoint, {
          params: {
            type,
            code,
          },
        });
      },
    },

    // lead
    leads: {
      /**
       * Get all user leads
       *
       * @param data
       * @param {string} data.startDateYyyyMmDd - start date
       * @param {string} data.endDateYyyyMmDd - end date
       *
       * @returns dispatch
       * */
      allLeads(params = {}) {
        const endpoint = `${RENTAL_MANAGER_API_V2_PREFIX}/users/listingConversation/getLatestConversations`;

        return requestWrapper(client, GET, endpoint, { params });
      },
      lead(data = {}) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/leads/details`;

        return requestWrapper(client, GET, endpoint, {
          params: {
            inquiryId: data.leadId,
            includeListingDetails: data.includeListingDetails,
          },
        });
      },
      // update inquiry status
      updateLeadStatus(data = {}) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/leads/inbox/status`;

        return requestWrapper(client, GET, endpoint, {
          params: {
            inquiryId: data.leadId,
            read: data.read,
          },
        });
      },
      addMessage(params = {}) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/listingConversation/addMessage`;

        return requestWrapper(client, GET, endpoint, { params });
      },
    },
    // posting path
    properties: {
      isApplicationsAccepted(propertyId, isApplicationsAccepted, requireUserName) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/isApplicationsAccepted`;

        return requestWrapper(client, GET, endpoint, {
          params: {
            propertyId,
            isApplicationsAccepted,
            requireUserName,
          },
        });
      },
      feedListingManagement: {
        createManualVersion(listingAlias) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/feedListingManagement/createManualVersion`;
          return requestWrapper(client, GET, endpoint, { params: { listingAlias } });
        },
      },
      amenities: {
        create(data) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/amenities/create`;
          return requestWrapper(client, GET, endpoint, {
            params: {
              propertyId: data.propertyId,
              text: data.amenityText,
            },
          });
        },
        delete(amenityId) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/amenities/delete`;
          return requestWrapper(client, GET, endpoint, { params: { amenityId } });
        },
      },
      apartmentAmenities: {
        // Creates and removes generalized unit amenities.
        create(data) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/apartmentAmenities/create`;
          return requestWrapper(client, GET, endpoint, {
            params: {
              propertyId: data.propertyId,
              text: data.amenityText,
              availableInSelectUnits: data.availableInSelectUnits,
            },
          });
        },
        delete(amenityId) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/apartmentAmenities/delete`;
          return requestWrapper(client, GET, endpoint, { params: { amenityId } });
        },
      },
      create(data) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/create`;
        return requestWrapper(client, GET, endpoint, {
          params: {
            propertyTypeCode: data.propertyTypeCode,
            listingTypeCode: data.listingTypeCode,
            // Optional
            fullAddress: data.fullAddress,
            unitNumber: data.unitNumber,
            numUnits: data.numUnits,
            customerId: data.customerId,
            includeWebAnalytics: true,
          },
        });
      },
      // accept optional reason param
      // accept optional comment param if reason is `OTHER`
      deactivate(propertyId, reason = null, comment = null) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/deactivate`;
        return requestWrapper(client, GET, endpoint, {
          params: {
            propertyId,
            reason,
            comment,
          },
        });
      },
      delete(propertyId, reason) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/delete`;
        return requestWrapper(client, GET, endpoint, {
          params: {
            propertyId,
            reason,
          },
        });
      },
      edit(data) {
        const { fieldName, additionalParams } = data;
        const fieldValue = data[fieldName];

        const params = {};
        params.propertyId = data.propertyId;
        params[fieldName] = fieldValue;

        if (additionalParams) {
          Object.keys(additionalParams).forEach((value) => (params[value] = additionalParams[value]));
        }

        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/${fieldName}`;

        return requestWrapper(client, GET, endpoint, { params });
      },
      externalTour: {
        associateModel(externalTour, encodedAlias, shouldAssociate, encodedFloorplanKey) {
          const path = shouldAssociate ? 'addExternalTourForModel' : 'removeExternalTourFromModel';
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/externalTour/${path}`;
          return requestWrapper(client, GET, endpoint, {
            params: {
              encodedAlias,
              encodedFloorplanKey,
              externalTour: {
                ...externalTour,
                tourType: externalTour?.tourType || 'ZRM',
              },
            },
          });
        },
        associateListing(externalTour, encodedAlias, shouldAssociate) {
          const path = shouldAssociate ? 'addExternalTourForListing' : 'removeExternalTourFromListing';
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/externalTour/${path}`;
          return requestWrapper(client, GET, endpoint, {
            params: {
              encodedAlias,
              externalTour,
            },
          });
        },
      },
      floorPlans: {
        layouts: {
          delete(floorPlanId) {
            const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/layouts/delete`;
            return requestWrapper(client, GET, endpoint, { params: { floorPlanId } });
          },
          upload({ formData, floorPlanId } = {}) {
            const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/layouts/upload`;
            return requestWrapper(fileClient, POST, endpoint, {
              data: formData,
              params: { floorPlanId },
            });
          },
        },
        photos: {
          caption(data) {
            const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/photos/caption`;
            return requestWrapper(client, GET, endpoint, {
              params: {
                photoId: data.photoId,
                caption: data.caption,
              },
            });
          },
          delete(photoId) {
            const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/photos/delete`;
            return requestWrapper(client, GET, endpoint, { params: { photoId } });
          },
          upload({ formData, floorPlanId } = {}) {
            const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/photos/upload`;
            return requestWrapper(fileClient, POST, endpoint, {
              params: { floorPlanId },
              data: formData,
            });
          },
        },
        units: {
          createV2(floorPlanId, params) {
            const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/units/createComplete`;
            return requestWrapper(client, GET, endpoint, {
              params: {
                floorPlanId,
                ...params,
              },
            });
          },
        },
        get(floorPlanId) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/details`;
          return requestWrapper(client, GET, endpoint, { params: { floorPlanId } });
        },
        delete(floorPlanId) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/floorPlans/delete`;
          return requestWrapper(client, GET, endpoint, { params: { floorPlanId } });
        },
      },
      markAcknowledgedLandlordMessage(actionId) {
        const endpoint = `${RENTAL_MANAGER_API_V2_PREFIX}/users/rentPayment/markAcknowledged`;
        return requestWrapper(client, POST, endpoint, { data: { actionId } });
      },
      // add boolean to params
      getDetails(propertyId) {
        const endpoint = `${RENTAL_MANAGER_API_V2_PREFIX}/users/properties/details`;
        return requestWrapper(client, GET, endpoint, {
          params: {
            propertyId,
            includeTopDupesDetails: true,
            includeVrModels: false,
            includeHasVrModels: false,
            includeWebAnalytics: true,
            includeInappropriatePhotosTag: true,
            includeDeliveryMethod: true,
          },
        });
      },
      getDetailsOverview(listingAlias, includeRestrictions = false) {
        const endpoint = `${RENTAL_MANAGER_API_V2_PREFIX}/users/properties/details/overview`;
        return requestWrapper(client, GET, endpoint, {
          params: {
            propertyId: listingAlias,
            includeRestrictions,
          },
        });
      },
      getListingAliasForListingId({ listingId, originalUrl }) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/getListingAliasForListingId`;
        return requestWrapper(client, GET, endpoint, {
          params: {
            listingId,
            originalUrl,
          },
        });
      },
      getStatus(propertyId) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/status`;
        return requestWrapper(client, GET, endpoint, { params: { propertyId } });
      },
      feedToManual: {
        statuses() {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/feedToManual/statuses`;
          return requestWrapper(client, GET, endpoint);
        },
        convertAll() {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/feedToManual/convertAll`;
          return requestWrapper(client, POST, endpoint);
        },
        convert(listingAlias) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/feedToManual/convert`;
          return requestWrapper(client, GET, endpoint, { params: { listingAlias } });
        },
      },
      /**
       * Get listings based on property type, location, and brand.
       *
       * @param {string} type - The type of listing to fetch
       * @param {object} params - The query params to pass to the endpoint
       *   @param {boolean} [startKeyExclusive=null] - return response after listing id
       *   @param {boolean} [limit=null] - Limit the number of responses
       *   @param {string} [searchText=''] - Limit the number of responses
       *   @param {boolean} [includeActive] - Include active listings
       *   @param {boolean} [includeInactive] - Include inactive listings
       * */
      listingInt(type, params) {
        // type for listings is statuses
        if (type === SelectPropertyConstants.LISTINGS) {
          type = 'statuses';
        }

        const queryParams = ({
          startKeyExclusive = '',
          limit = 20,
          searchText = '',
          includeActive = true,
          includeInactive = true,
        }) => ({
          startKeyExclusive: String(startKeyExclusive),
          limit: Number(limit),
          searchText: String(searchText),
          includeActive: Boolean(includeActive),
          includeInactive: Boolean(includeInactive),
        });

        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/listings/${type}`;

        return requestWrapper(client, GET, endpoint, { params: queryParams(params) });
      },
      pets(params) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/pets`;
        return requestWrapper(client, GET, endpoint, { params });
      },
      unitAmenities: {
        create(data) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/unitAmenities/create`;
          return requestWrapper(client, GET, endpoint, {
            params: {
              floorPlanId: data.floorPlanId,
              text: data.amenityText,
              availableInSelectUnits: data.availableInSelectUnits,
            },
          });
        },
        delete(amenityId) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/unitAmenities/delete`;
          return requestWrapper(client, GET, endpoint, { params: { amenityId } });
        },
      },
      payments: {
        defaultPaymentMethod(listingAlias) {
          const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/payments/getDefaultPaymentMethod`;
          return requestWrapper(client, GET, endpoint, { params: { listingId: listingAlias } });
        },
      },
      convertFloorplanOrUnitToNucleus(modelId, unit) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/convert/multiToSingleFamily`;
        return requestWrapper(client, GET, endpoint, { params: { modelId, unit } });
      },
    },
    emailVerification: {
      unverifiedEmailFeedListings(params = {}) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/emailVerification/unverifiedEmailFeedListings`;
        return requestWrapper(client, GET, endpoint, { params });
      },
      verifySecurityToken(securityToken) {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/emailVerification/verifySecurityToken`;
        return requestWrapper(client, GET, endpoint, { params: { securityToken } });
      },
    },
    company: {
      getUserManagementProfiles() {
        const endpoint = `${RENTAL_MANAGER_API_V1_PREFIX}/users/company/getUserManagementProfiles`;
        return requestWrapper(client, GET, endpoint);
      },
    },
  },
};

export default api;
